// iter142 — Active Ride screen at /ride/active/:rideId.
// Shows a running timer + 3 buttons:
//   • End Ride → marks ride completed and opens RideReviewSheet.
//   • SOS → POST /rides/{id}/sos then opens WhatsApp directly with the
//     same phone number used at dispatch, message pre-filled.
//   • Show My QR → bottom sheet rendering the user's QR (re-uses /my-qr
//     content via an iframe-free copy of the relevant block).
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, X, Loader2, AlertTriangle, QrCode, Square } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import { openWhatsApp } from "../lib/openWhatsApp";
import RideReviewSheet from "../components/RideReviewSheet";

function fmtElapsed(ms) {
  if (ms < 0 || !Number.isFinite(ms)) ms = 0;
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function readClientId() {
  try { return localStorage.getItem("habano.client_id") || null; } catch { return null; }
}

export default function ActiveRide() {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const [ride, setRide] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [reviewOpen, setReviewOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [confirmedAt] = useState(() => Date.now());

  useEffect(() => {
    const cid = readClientId();
    api.get(`/rides/${rideId}${cid ? `?client_id=${encodeURIComponent(cid)}` : ""}`)
      .then(({ data }) => setRide(data))
      .catch(() => setRide(null))
      .finally(() => setLoaded(true));
  }, [rideId]);

  useEffect(() => {
    const i = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  // QR encodes /ride/customer/<rideId> so a driver scan lands on the
  // customer-info + Save Customer page (Phase B of this feature).
  const qrValue = `${typeof window !== "undefined" ? window.location.origin : ""}/ride/customer/${rideId}`;

  const elapsed = nowTs - confirmedAt;

  const endRide = async () => {
    try {
      const cid = readClientId();
      await api.post(`/rides/${rideId}/end`, { client_id: cid });
      toast.success(lang === "es" ? "Viaje finalizado" : "Ride ended");
    } catch { /* still allow review */ }
    setReviewOpen(true);
  };

  const sos = () => {
    // iter143 — Open WhatsApp FIRST synchronously inside the click handler
    // (popup-blocker safe, same pattern as the dispatch flow). Side
    // effects (GPS + admin alert) fire AFTER the open as fire-and-forget.
    const phone = ride?.phone_target || "";
    const msg = lang === "es"
      ? "EMERGENCIA - Necesito ayuda. Soy usuario de Habano App."
      : "EMERGENCY - I need help. I am a Habano App user.";
    const opened = openWhatsApp(phone, msg);
    if (!opened) {
      toast.error(lang === "es" ? "No se pudo abrir WhatsApp" : "Could not open WhatsApp");
      return;
    }
    // Fire-and-forget: best-effort GPS + admin alert post.
    (async () => {
      let lat = null, lng = null;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation
          ? navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })
          : rej(new Error("no geo")));
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch { /* allow SOS without GPS */ }
      try {
        const cid = readClientId();
        await api.post(`/rides/${rideId}/sos`, { client_id: cid, lat, lng });
      } catch { /* swallow — WhatsApp already opened */ }
    })();
  };

  if (!loaded) {
    return (
      <main className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40" />
      </main>
    );
  }
  if (!ride) {
    return (
      <main className="min-h-screen grid place-items-center px-6 bg-background" data-testid="active-ride-not-found">
        <div className="text-center">
          <h1 className="font-display text-2xl text-foreground">{lang === "es" ? "Viaje no encontrado" : "Ride not found"}</h1>
          <Link to="/rides/history" className="btn-outline mt-4 inline-flex">{lang === "es" ? "Mi historial" : "My history"}</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="active-ride-page" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      <header className="px-5 flex items-center gap-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 28px)" }}>
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="active-ride-back"
          className="grid place-items-center" style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0">
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#E8B84B",
                         letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}>
            {lang === "es" ? "Viaje en curso" : "Active ride"}
          </div>
          <h1 data-testid="active-ride-title"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#F5F1E8",
                     whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {ride.destination_name}
          </h1>
        </div>
      </header>

      <section className="px-5 mt-8 text-center">
        <div data-testid="active-ride-timer"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 64, color: "#F5F1E8", lineHeight: 1,
                   letterSpacing: "0.04em" }}>
          {fmtElapsed(elapsed)}
        </div>
        <div className="mt-2"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}>
          {lang === "es" ? "Tiempo desde que confirmaste" : "Time since you confirmed"}
        </div>
      </section>

      <section className="px-5 mt-10 space-y-3" data-testid="active-ride-actions">
        <button type="button" data-testid="active-ride-end"
          onClick={endRide}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ height: 56, background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
                   color: "#0B0B0B", border: "none", borderRadius: 14,
                   fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>
          <Square className="w-4 h-4" strokeWidth={2} />
          {lang === "es" ? "Finalizar viaje" : "End Ride"}
        </button>
        <button type="button" data-testid="active-ride-sos"
          onClick={sos}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ height: 56, background: "#E66B6B", color: "#fff", border: "none", borderRadius: 14,
                   fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>
          <AlertTriangle className="w-4 h-4" strokeWidth={2} />
          {lang === "es" ? "SOS · Emergencia" : "SOS"}
        </button>
        <button type="button" data-testid="active-ride-show-qr"
          onClick={() => setQrOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ height: 56, padding: "0 28px", background: "transparent", color: "#E8B84B",
                   border: "1px solid rgba(232,184,75,0.5)", borderRadius: 14,
                   fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600 }}>
          <QrCode className="w-4 h-4" strokeWidth={1.75} />
          {lang === "es" ? "Mostrar mi QR" : "Show My QR"}
        </button>
        <p className="text-center px-2" data-testid="active-ride-qr-sub"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#9A9490",
                   lineHeight: 1.4, marginTop: -4 }}>
          {lang === "es"
            ? "Muestra este QR a tu conductor para guardarlo en futuros viajes"
            : "Show this QR to your driver to save them for future rides"}
        </p>
      </section>

      {qrOpen && (
        <>
          <button type="button" aria-label="Dismiss" data-testid="active-ride-qr-scrim"
            onClick={() => setQrOpen(false)}
            className="fixed inset-0 z-[80]" style={{ background: "rgba(0,0,0,0.78)" }} />
          <div data-testid="active-ride-qr-sheet"
            className="fixed left-0 right-0 bottom-0 z-[81]"
            style={{ background: "#171717", borderTopLeftRadius: 20, borderTopRightRadius: 20,
                     borderTop: "1px solid rgba(232,184,75,0.3)",
                     paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
            <div style={{ position: "relative" }}>
              <div className="grid place-items-center pt-3 pb-2">
                <span style={{ width: 44, height: 4, borderRadius: 9999, background: "rgba(232,184,75,0.45)" }} />
              </div>
              <div className="px-6 pt-2 pb-4 text-center">
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F5F1E8" }}>
                  {lang === "es" ? "Tu QR" : "Your QR"}
                </h2>
                <p className="mt-2 mx-auto" style={{ maxWidth: 280,
                  fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", lineHeight: 1.5 }}>
                  {lang === "es"
                    ? "Comparte tu QR con tu conductor si deseas usarlo en el futuro."
                    : "Share your QR with your driver if you wish to use them in the future."}
                </p>
              </div>
              <button type="button" data-testid="active-ride-qr-close" onClick={() => setQrOpen(false)}
                aria-label="Close" className="grid place-items-center"
                style={{ position: "absolute", top: 12, right: 14, width: 28, height: 28,
                         borderRadius: "50%", background: "#222", color: "#fff", border: "none" }}>
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            <div className="grid place-items-center pb-8">
              <div data-testid="active-ride-qr-img"
                style={{ background: "#0B0B0B", padding: 16, borderRadius: 16,
                         border: "1px solid rgba(232,184,75,0.3)" }}>
                <QRCodeCanvas value={qrValue} size={260} bgColor="#0B0B0B" fgColor="#F5F1E8"
                  includeMargin={false} level="M" />
              </div>
            </div>
          </div>
        </>
      )}

      <RideReviewSheet
        isOpen={reviewOpen}
        onClose={() => setReviewOpen(false)}
        rideId={rideId}
        onSubmitted={() => {
          // iter158 — After review submit, return to home (never
          // back to the countdown/active screen). Home.jsx handles the
          // mode gate so Ride Mode users land on Ride Mode and Discover
          // users land on Discover.
          setReviewOpen(false);
          navigate("/", { replace: true });
        }}
      />

      <BottomNav />
    </main>
  );
}
